/**
 * Thin fetch wrapper for the rbkr backend.
 *
 * Cookies (rbkr_access / rbkr_refresh) carry auth — we just send
 * `credentials: "include"` so the browser handles them. The API base
 * is "/v1" both in dev (Vite proxies to backend) and prod (same origin).
 */

import type { Envelope } from "@/types/api"

const BASE = "/v1"

export class ApiError extends Error {
  status: number
  code: string
  details?: unknown
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  { unwrap = true }: { unwrap?: boolean } = {},
): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE}${path}`
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
    ...init,
  })

  if (!res.ok) {
    let code = "HTTP_ERROR"
    let message = `${res.status} ${res.statusText}`
    let details: unknown = undefined
    try {
      const body = await res.json()
      if (body?.detail && typeof body.detail === "object") {
        code = body.detail.code ?? code
        message = body.detail.message ?? message
        details = body.detail.details
      } else if (typeof body?.detail === "string") {
        message = body.detail
      }
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, code, message, details)
  }

  if (res.status === 204) return undefined as T
  const json = await res.json()
  if (unwrap && json && typeof json === "object" && "data" in json) {
    // Caller may still want meta — expose via a hidden property.
    return json.data as T
  }
  return json as T
}

export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: "GET" }),
  getEnvelope: <T>(path: string, init?: RequestInit) =>
    request<Envelope<T>>(path, { ...init, method: "GET" }, { unwrap: false }),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, { ...init, method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, { ...init, method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: "DELETE" }),
}
